import {Character} from "@/characters/model/Character.ts";
import {formattedModifier} from "@/characters/model/AttributeStat.ts";
import {cn} from "@/lib/utils.ts";
import {CharacterDetailRow} from "@/characters/admin/details/useCharacterDetailsViewModel.ts";

const colorVariants = {
    highest: "text-emerald-300",
    high: "text-emerald-400",
    none: "text-foreground",
    low: "text-rose-400",
    lowest: "text-rose-300",
};

function getAttributeColor(modifier: number): string {
    if (modifier > 4) return colorVariants.highest;
    if (modifier > 0) return colorVariants.high;
    if (modifier < -4) return colorVariants.lowest;
    if (modifier < 0) return colorVariants.low;
    return colorVariants.none;
}

export default function CharacterStats({character, detailRows}: {
    character: Character;
    detailRows: CharacterDetailRow[];
}) {
    return <>
        <div className="mx-4 mt-3 grid grid-flow-col auto-cols-fr divide-x divide-border overflow-hidden rounded-md border border-border bg-surface/55"
             style={{fontVariant: "small-caps"}}>
            {character.hitPoints &&
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 px-3 py-1.5">
                    <span className="text-xs font-semibold text-muted-foreground">Trefferpunkte</span>
                    <strong className="text-lg leading-none text-warning">{character.hitPoints.value}</strong>
                    {character.hitPoints.formula &&
                        <span className="text-[0.7rem] text-muted-foreground">({character.hitPoints.formula})</span>}
                </div>}
            {character.armorClass &&
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 px-3 py-1.5">
                    <span className="text-xs font-semibold text-muted-foreground">Rüstungsklasse</span>
                    <strong className="text-lg leading-none text-warning">{character.armorClass.value}</strong>
                    {character.armorClass.info &&
                        <span className="text-[0.7rem] capitalize text-muted-foreground">{character.armorClass.info}</span>}
                </div>}
        </div>

        {character.attributes.length > 0 &&
            <div className="px-4 py-2">
                <div className="overflow-x-auto rounded-md border border-border bg-surface/45">
                    <table className="w-full min-w-[28rem] table-fixed border-collapse text-center text-xs">
                        <thead className="border-b border-border bg-surface-overlay/70 text-muted-foreground">
                        <tr>
                            <th scope="col" className="w-24 px-2 py-1 font-semibold">Attribute</th>
                            {character.attributes.map(attribute =>
                                <th scope="col" className="border-l border-border px-1 py-1 font-semibold uppercase"
                                    key={attribute.stat}>{attribute.stat}</th>)}
                        </tr>
                        </thead>
                        <tbody>
                        <AttributeRow label="Wert">
                            {character.attributes.map(attribute =>
                                <td key={`${attribute.stat}-value`}
                                    className={cn("border-l border-border px-1 py-0.5", getAttributeColor(attribute.modifier))}>
                                    {attribute.value}
                                </td>)}
                        </AttributeRow>
                        <AttributeRow label="Modifikator">
                            {character.attributes.map(attribute =>
                                <td key={`${attribute.stat}-modifier`}
                                    className={cn("border-l border-border px-1 py-0.5 font-bold", getAttributeColor(attribute.modifier))}>
                                    {formattedModifier(attribute.modifier)}
                                </td>)}
                        </AttributeRow>
                        <AttributeRow label="Rettung" last>
                            {character.attributes.map(attribute =>
                                <td key={`${attribute.stat}-save`}
                                    className={cn("border-l border-border px-1 py-0.5 font-bold", getAttributeColor(attribute.save))}>
                                    {formattedModifier(attribute.save)}
                                </td>)}
                        </AttributeRow>
                        </tbody>
                    </table>
                </div>
            </div>}

        {detailRows.length > 0 &&
            <dl className="mx-4 mb-3 grid grid-cols-[minmax(7.5rem,9rem)_auto] overflow-hidden rounded-md border border-border bg-surface/35 text-xs">
                {detailRows.map((row, index) =>
                    <DetailRow key={row.label} isLast={index === detailRows.length - 1} {...row}/>)}
            </dl>}
    </>;
}

function AttributeRow({label, last = false, children}: {
    label: string;
    last?: boolean;
    children: React.ReactNode;
}) {
    return <tr className={cn(!last && "border-b border-border")}>
        <th scope="row" className="bg-surface-overlay/25 px-2 py-0.5 text-left font-normal text-muted-foreground">
            {label}
        </th>
        {children}
    </tr>;
}

function DetailRow({label, values, isLast}: CharacterDetailRow & {isLast: boolean}) {
    return <>
        <dt className={cn(
            "border-r border-border bg-surface-overlay/30 px-2 py-1 font-semibold text-muted-foreground",
            !isLast && "border-b",
        )} style={{fontVariant: "small-caps"}}>
            {label}
        </dt>
        <dd className={cn("min-w-0 break-words px-2 py-1", !isLast && "border-b border-border")}>
            {values.join(", ")}
        </dd>
    </>;
}
